/**
 * Kullanıcı olarak giriş yap (#64) — taklit oturumu YENİ SEKMEDE ve yalnız o sekmeye ait açılır.
 *
 * Yöneticinin kendi oturumu açık kalır: taklit sekmesi token'larını yalnız sessionStorage'da
 * tutar (localStorage'daki yönetici oturumuna dokunmaz) ve sekmeler arası oturum kanalını
 * dinlemez — yönetici bir sekmede çıkış yapınca taklit sekmesi düşmesin, taklit bitince de
 * yönetici sekmeleri etkilenmesin.
 *
 * Devir: yönetici sekmesi `/?imp=<id>` adresini (kullanıcı hareketi kaybolmadan, hemen) açar,
 * ardından sunucudan taklit oturumunu alıp localStorage'a kısa ömürlü bir anahtarla bırakır.
 * Yeni sekme anahtarı okur ve SİLER; refresh token localStorage'da en fazla birkaç saniye kalır.
 */
import type { LoginResponse } from '@/shared/types/api';

const FLAG_KEY = 'octo.web.imp';
const HANDOFF_PREFIX = 'octo.web.imp.handoff.';
const HANDOFF_TTL = 60_000;

export interface ImpersonationHandoff {
  refreshToken?: string;
  user?: LoginResponse;
  connectionId?: string | null;
  connectionName?: string | null;
  sessionId?: string;
  error?: string;
  at: number;
}

/** Adres çubuğundaki `?imp=` → sekme bayrağı. Modül yüklenirken (oturum kanalından önce) çalışır. */
function captureFromUrl() {
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('imp');
    if (!id) return;
    sessionStorage.setItem(FLAG_KEY, id);
    url.searchParams.delete('imp');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
  } catch {
    /* noop */
  }
}
if (typeof window !== 'undefined') captureFromUrl();

export const impersonation = {
  /** Bu sekme bir taklit oturumu mu (ya da onu bekliyor mu)? */
  isTab(): boolean {
    try {
      return !!sessionStorage.getItem(FLAG_KEY);
    } catch {
      return false;
    }
  },
  pendingId(): string | null {
    try {
      return sessionStorage.getItem(FLAG_KEY);
    } catch {
      return null;
    }
  },
  endTab() {
    try {
      sessionStorage.removeItem(FLAG_KEY);
    } catch {
      /* noop */
    }
  },
  newId(): string {
    return crypto.randomUUID();
  },
  put(id: string, data: Omit<ImpersonationHandoff, 'at'>) {
    try {
      localStorage.setItem(HANDOFF_PREFIX + id, JSON.stringify({ ...data, at: Date.now() }));
    } catch {
      /* noop */
    }
  },
  /** Devri bir kez okur ve siler; süresi geçmişse yok sayar. */
  take(id: string): ImpersonationHandoff | null {
    try {
      const key = HANDOFF_PREFIX + id;
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      localStorage.removeItem(key);
      const data = JSON.parse(raw) as ImpersonationHandoff;
      return Date.now() - data.at > HANDOFF_TTL ? null : data;
    } catch {
      return null;
    }
  },
  /** Devir anahtarı yazılana kadar bekler (yönetici sekmesi isteği henüz bitirmemiş olabilir). */
  async waitFor(id: string, timeoutMs = 30_000): Promise<ImpersonationHandoff | null> {
    const deadline = Date.now() + timeoutMs;
    for (;;) {
      const data = this.take(id);
      if (data || Date.now() > deadline) return data;
      await new Promise((r) => setTimeout(r, 150));
    }
  },
  /** Süresi geçmiş, okunmamış devirleri temizler (sekme hiç açılamadıysa). */
  sweep() {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key?.startsWith(HANDOFF_PREFIX)) continue;
        try {
          const at = (JSON.parse(localStorage.getItem(key) ?? '{}') as { at?: number }).at ?? 0;
          if (Date.now() - at > HANDOFF_TTL) localStorage.removeItem(key);
        } catch {
          localStorage.removeItem(key);
        }
      }
    } catch {
      /* noop */
    }
  },
};
